// Persistent personal display profile. Auth0 remains the login authority.
import React, { useCallback, useEffect, useState } from "react";
import "./Profile.css";

const empty = { first_name: "", last_name: "", preferred_name: "" };

export default function Profile({ apiBaseUrl, tenantId, userId, getAccessToken }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const request = useCallback(async (method, body) => {
    const token = await getAccessToken();
    const response = await fetch(`${apiBaseUrl}/account/profile`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenantId,
        "X-User-Id": userId,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw new Error(`Profile request failed (${response.status})`);
    return response.json();
  }, [apiBaseUrl, tenantId, userId, getAccessToken]);

  const reload = useCallback(async () => {
    if (!tenantId || !userId || !getAccessToken || !apiBaseUrl) return;
    setLoading(true);
    setError("");
    try {
      const data = await request("GET");
      setProfile(data);
      setForm({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        preferred_name: data.preferred_name || "",
      });
    } catch (e) {
      setError(e.message || "Unable to load your profile.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId, apiBaseUrl, getAccessToken, request]);

  useEffect(() => {
    setProfile(null);
    setMessage("");
    if (tenantId && userId) reload();
    else setLoading(false);
  }, [tenantId, userId, reload]);

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await request("PATCH", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        preferred_name: form.preferred_name.trim(),
      });
      setProfile(data);
      setForm({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        preferred_name: data.preferred_name || "",
      });
      setEditing(false);
      setMessage("Profile saved. Your welcome greeting has been updated.");
      window.dispatchEvent(new Event("oasys:personal-profile-updated"));
    } catch (e) {
      setError(e.message || "Unable to save your profile.");
    } finally {
      setSaving(false);
    }
  };

  if (!tenantId || !userId) return <div className="settings-inline-state">An active OASYS account is required to edit your profile.</div>;
  if (loading) return <div className="settings-inline-state">Loading profile…</div>;
  if (!profile) return <div className="settings-inline-state settings-inline-error">{error || "Profile unavailable."}</div>;

  const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || profile.display_name || "My profile";
  return (
    <div className="settings-profile-shell">
      <section className="settings-profile-card">
        <div className="settings-profile-card-header">
          <div><h3>Personal profile</h3><p>Your display information is saved to your OASYS account and persists between logins.</p></div>
          {!editing && <button type="button" className="settings-secondary-button" onClick={() => { setEditing(true); setMessage(""); }}>Edit profile</button>}
        </div>
        {message && <p role="status">{message}</p>}
        {error && <p role="alert" className="settings-inline-error">{error}</p>}
        {editing ? (
          <form className="settings-profile-form" onSubmit={save}>
            {[["first_name", "First name"], ["last_name", "Last name"], ["preferred_name", "Preferred name for greeting"]].map(([key, label]) => (
              <label key={key}><span>{label}</span><input value={form[key]} maxLength={80} onChange={(e) => setForm(old => ({...old, [key]: e.target.value}))} disabled={saving} /></label>
            ))}
            <div className="settings-profile-actions">
              <button type="submit" className="settings-primary-button" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
              <button type="button" className="settings-secondary-button" disabled={saving} onClick={() => { setEditing(false); setError(""); }}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="settings-profile-grid">
            <div><span>Full name</span><strong>{name}</strong></div>
            <div><span>Preferred name</span><strong>{profile.preferred_name || "Not set"}</strong></div>
            <div className="settings-profile-grid-wide"><span>Sign-in email</span><strong className="settings-profile-readonly-email">{profile.email || "—"}</strong></div>
            <div className="settings-profile-grid-wide"><span>Profile photo</span><strong>Photo upload is coming in the next profile slice.</strong></div>
          </div>
        )}
      </section>
    </div>
  );
}
